import type { IUser } from '@/types/user';
import { IUserVideoNewsPlayerSample } from '@/types/user';
import { IProfileInfo } from '@/types/profile';

export enum UserTypeEnum {
  Broker = 'broker',
  Mortgage = 'mortgage',
  CommercialAgent = 'commercial_agent',
  IndividualAgent = 'individual_agent',
  Insurance = 'insurance',
  Finance = 'finance',
}

export type UserTypeIdName =
  | 'broker'
  | 'mortgage'
  | 'commercial_agent'
  | 'individual_agent'
  | 'insurance'
  | 'finance';

export type Industry =
  | 'real_estate'
  | 'insurance'
  | 'finance'
  | 'mortgage'
  | 'commercial_real_estate'
  | string;

// Available industry constants
export const INDUSTRIES = {
  REAL_ESTATE: 'real_estate',
  COMMERCIAL_REAL_ESTATE: 'commercial_real_estate',
  INSURANCE: 'insurance',
  FINANCE: 'finance',
  MORTGAGE: 'mortgage',
} as const;

// Array of all available industries
export const INDUSTRY_OPTIONS: Industry[] = Object.values(INDUSTRIES);

// Mapping from selectUserTypeIndustry values to Industry type
export const USER_TYPE_INDUSTRY_TO_INDUSTRY_MAP: Record<string, Industry> = {
  'Real Estate': 'real_estate',
  'Commercial Real Estate': 'commercial_real_estate',
  Insurance: 'insurance',
  Finance: 'finance',
  Mortgage: 'mortgage',
} as const;

// Helper function to convert selectUserTypeIndustry value to Industry type
export const getUserIndustryType = (userTypeIndustry: string): Industry => {
  return USER_TYPE_INDUSTRY_TO_INDUSTRY_MAP[userTypeIndustry] || 'real_estate';
};

export const getUserTypeIdName = (
  userTypeId: IUser['user_type_id']
): UserTypeIdName => {
  switch (userTypeId) {
    case 8:
    case 5:
      return 'broker';
    case 6:
      return 'mortgage';
    case 7:
      return 'commercial_agent';
    case 11:
      return 'insurance';
    case 12:
      return 'finance';
    case 2:
    default:
      return 'individual_agent';
  }
};

export const getUserTypeIndustry = (
  userTypeId: IUser['user_type_id']
): string => {
  switch (userTypeId) {
    case 6:
      return 'Mortgage';
    case 7:
      return 'Commercial Real Estate';
    case 11:
      return 'Insurance';
    case 12:
      return 'Finance';
    case 2:
    case 10:
    case 8:
    case 5:
    default:
      return 'Real Estate';
  }
};

export const getFullUserTypeIndustry = (
  userTypeId: IUser['user_type_id']
): string => {
  switch (userTypeId) {
    case 6:
      return 'Mortgage Broker';
    case 7:
      return 'Commercial Real Estate Agent';
    case 11:
      return 'Insurance Agent';
    case 12:
      return 'Financial Advisor';
    case 2:
    case 10:
    case 8:
    case 5:
    default:
      return 'Real Estate Agent';
  }
};

export const getUserTypeId = (userTypeIdName: UserTypeIdName) => {
  switch (userTypeIdName) {
    case 'broker':
      return 8;
    case 'mortgage':
      return 6;
    case 'commercial_agent':
      return 7;
    case 'insurance':
      return 11;
    case 'finance':
      return 12;
    case 'individual_agent':
    default:
      return 2;
  }
};

export function isUSAUser(user: IUser) {
  return user?.country === 'United States';
}

export function isCanadianUser(user: IUser) {
  return user?.country === 'Canada';
}

export function getUserPremiumIntroRandomizer(
  user: IUser | IUserVideoNewsPlayerSample | IProfileInfo | Partial<IUser>,
  id: number
) {
  // use formatted phone
  user = { ...user, phone: user?.formatted_phone || user?.phone };

  // handle premium intro randomizer
  if (
    user?.videoOptions?.use_randomize_premium_branding &&
    user?.videoOptions?.randomize_premium_branding_templates.length > 0
  ) {
    const index =
      id % user?.videoOptions?.randomize_premium_branding_templates.length;
    const randomPremiumBrandingId =
      user?.videoOptions?.randomize_premium_branding_templates[index];
    const randomPremiumBranding = user?.premium_intros.find(
      (intro) => intro.template?.id == randomPremiumBrandingId
    );

    if (randomPremiumBranding) {
      return { ...user, premium_intro: randomPremiumBranding };
    }
  }

  return user;
}

/**
 * Always return insurance user type id for now
 * @param _source
 * @returns
 */
export function getUserTypeIdFromSource(_source: string) {
  return getUserTypeId('insurance');
}

export const userTypeOptions = [
  {
    label: 'Individual Real Estate Agent',
    value: getUserTypeId(UserTypeEnum.IndividualAgent),
  },
  {
    label: 'Managing Broker (Real Estate)',
    value: getUserTypeId(UserTypeEnum.Broker),
  },
  {
    label: 'Commercial (Real Estate)',
    value: getUserTypeId(UserTypeEnum.CommercialAgent),
  },
  {
    label: 'Mortgage Broker / Loan Officer',
    value: getUserTypeId(UserTypeEnum.Mortgage),
  },
  { label: 'Insurance Agent', value: getUserTypeId(UserTypeEnum.Insurance) },
  { label: 'Financial Advisor', value: getUserTypeId(UserTypeEnum.Finance) },
];
